import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

import useProjectService from '../../app/services/hooks/useProjectService';
import { ProjectDataForm, ProjectFilters } from '../../app/models/Project';
import ProjectDataTable from './components/tables/ProjectDataTable';
import ProjectCreate from './components/ProjectCreate';
import ProjectEdit from './components/ProjectEdit';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'proyectos', url: '/projects', isActive: true }
];

const Projects = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetProjects, getProjects, deleteProject } = useProjectService();

  const [projects, setProjects] = useState<ProjectDataForm[]>([]);
  const [projectIdEdit, setProjectIdEdit] = useState<number>(-1);
  const [showingEdit, setShowingEdit] = useState(false);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);

  const [projectFilter, setProjectFilter] = useState<ProjectFilters>({
    page: 1, per_page: 10, sort: 'id', order: 'desc'
  });

  useEffect(() => { reloadTable(); }, [projectFilter]);

  const getAll = () => {
    getProjects(projectFilter, {
      onSuccess: (response: ServiceResponse) => {
        setProjects(response.data.projects);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const showEdit = (id: number) => { setShowingEdit(true); setProjectIdEdit(id); };
  const hideEdit = () => { setShowingEdit(false); setProjectIdEdit(-1); };
  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);
  const reloadTable = () => getAll();

  const destroy = (id: number) => {
    const _text = 'Está a punto de eliminar el proyecto #' + id;
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Si, confirmar',
          onClick: () => {
            deleteProject(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'El proyecto no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Proyectos" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nuevo proyecto" />} />
        <ProjectDataTable
          projects={projects}
          edit={showEdit}
          destroy={destroy}
          loading={fetchingGetProjects}
          filter={projectFilter}
          setFilter={setProjectFilter}
          totalRows={totalRows}
          callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal show={showingCreate} handleClose={hideCreate} title="Crear proyecto" backdrop={true} showFooter={false}>
          <ProjectCreate onSaved={() => { reloadTable(); hideCreate(); }} onCancel={hideCreate} />
        </DefaultModal>
      )}

      {showingEdit && (
        <DefaultModal show={showingEdit} handleClose={hideEdit} title="Editar proyecto" backdrop={true} showFooter={false}>
          <ProjectEdit projectId={projectIdEdit} onSaved={() => { reloadTable(); hideEdit(); }} onCancel={hideEdit} onError={hideEdit} />
        </DefaultModal>
      )}
    </>
  );
};

export default Projects;
